import { useState, useCallback } from 'react';
import EnumsService from '../Services/enums.service';

/**
 * Reusable hook to fetch account card data and manage the AccountCardReportHTML modal state.
 * Includes a month-range picker phase before fetching.
 *
 * @param {{ client, token, accountCardOptions, isMonthly }} deps
 */
export default function useAccountCardReport({ client, token, accountCardOptions, isMonthly }) {
    const [reportOpen, setReportOpen] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [sortedDataForPdf, setSortedDataForPdf] = useState(null);
    const [firstRowDetails, setFirstRowDetails] = useState(null);
    const [finalBalance, setFinalBalance] = useState('0');
    const [palCodeCategoryOptions, setPalCodeCategoryOptions] = useState([]);
    const [selectedCardsForReport, setSelectedCardsForReport] = useState([]);

    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [pickerStartDate, setPickerStartDate] = useState(null);
    const [pickerEndDate, setPickerEndDate] = useState(null);
    const [pendingCard, setPendingCard] = useState(null);

    const handleSortConfigChange = useCallback((newSortConfig) => {
        setSortedDataForPdf(prev => {
            if (!prev?.transactions) return prev;

            const sorted = [...prev.transactions].sort((a, b) => {
                const { key, type } = newSortConfig;
                const dir = newSortConfig.direction === 'ascending' ? 1 : -1;

                let valA = a[key];
                let valB = b[key];

                if (type === 'date') return dir * (new Date(valA) - new Date(valB));
                if (type === 'number') return dir * ((Number(valA) || 0) - (Number(valB) || 0));
                return dir * String(valA || '').localeCompare(String(valB || ''));
            });

            return { ...prev, transactions: sorted };
        });
    }, []);

    const closeReport = useCallback(() => {
        setReportOpen(false);
        setSortedDataForPdf(null);
        setFirstRowDetails(null);
        setFinalBalance('0');
        setSelectedCardsForReport([]);
    }, []);

    const closeDatePicker = useCallback(() => {
        setDatePickerOpen(false);
        setPendingCard(null);
    }, []);

    const promptReport = useCallback((cardId, reportingMonthDate) => {
        if (!client || !token) return;

        const accountCard = accountCardOptions?.find(
            (r) => r.cardId === cardId || r.id === cardId
        );
        if (!accountCard) return;

        const ref = reportingMonthDate ? new Date(reportingMonthDate) : new Date();
        const year = ref.getUTCFullYear();

        setPendingCard(accountCard);
        setPickerStartDate(new Date(Date.UTC(year, 0, 1)));
        setPickerEndDate(new Date(Date.UTC(year, 11, 1)));
        setDatePickerOpen(true);
    }, [client, token, accountCardOptions]);

    const toDay10 = (d) => {
        if (!d) return d;
        const dt = new Date(d);
        return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 10));
    };

    const fetchReport = useCallback((overrideStart, overrideEnd) => {
        if (!pendingCard || !client || !token) return;

        setDatePickerOpen(false);
        setReportOpen(true);
        setReportLoading(true);
        setSelectedCardsForReport([pendingCard]);

        const fetchStart = toDay10(overrideStart);
        const fetchEnd = toDay10(overrideEnd);
        const accountCard = pendingCard;

        (async () => {
            try {
                const palCatResp = EnumsService.GetPalCodeCategoriesByAccountantId(client.accountantId, token);

                const response = await EnumsService.AccountCardPalCodeDetailsWithFetchType(
                    [accountCard.cardId],
                    [accountCard.name],
                    [accountCard.palCode || 'NonPalCode'],
                    client.accountantId,
                    client.caseId,
                    fetchStart,
                    fetchEnd,
                    !isMonthly,
                    false,
                    1,
                    token
                );

                let palCatData = [];
                try {
                    const palCatResponse = await palCatResp;
                    if (palCatResponse.ok) palCatData = await palCatResponse.json();
                } catch { /* non-critical */ }
                setPalCodeCategoryOptions(palCatData);

                if (!response?.ok) {
                    setReportLoading(false);
                    return;
                }

                const result = await response.json();

                let allTransactions = [];
                let totalCreditAmount = 0;
                let totalDebitAmount = 0;
                let totalBalance = 0;

                if (result.accountCards?.length > 0) {
                    result.accountCards.forEach(ac => {
                        if (ac.transactions?.length > 0) {
                            const cardTxns = ac.transactions.map(tx => ({
                                ...tx,
                                cardId: ac.accountCardId,
                                cardName: ac.accountCardName,
                                palCode: ac.palCode,
                            }));
                            allTransactions.push(...cardTxns);
                        }
                        totalCreditAmount += ac.creditAccountAmount || 0;
                        totalDebitAmount += ac.debitAccountAmount || 0;
                    });
                    allTransactions.sort((a, b) => new Date(a.documentDate) - new Date(b.documentDate));
                    totalBalance = totalDebitAmount - totalCreditAmount;

                    if (result.palCode && Array.isArray(result.palCode) &&
                        result.palCodeCategory && Array.isArray(result.palCodeCategory)) {
                        result.accountCards = result.accountCards.map(ac => {
                            if (!ac.palCode || ac.palCode.trim() === '') {
                                return { ...ac, palCodeCategory: '-', palCodeAccountGroup: '-', palCodeType: '-' };
                            }
                            const idx = result.palCode.findIndex(p => p === ac.palCode);
                            if (idx !== -1) {
                                return {
                                    ...ac,
                                    palCodeCategory: result.palCodeCategory[idx] || '-',
                                    palCodeAccountGroup: Array.isArray(result.palCodeAccountGroup) ? result.palCodeAccountGroup[idx] || '-' : result.palCodeAccountGroup || '-',
                                    palCodeType: Array.isArray(result.palCodeType) ? result.palCodeType[idx] || '-' : result.palCodeType || '-',
                                };
                            }
                            return { ...ac, palCodeCategory: ac.palCode, palCodeAccountGroup: '-', palCodeType: '-' };
                        });
                    }
                } else {
                    allTransactions = result.transactions || [];
                    totalCreditAmount = result.creditAccountAmount || 0;
                    totalDebitAmount = result.debitAccountAmount || 0;
                    totalBalance = result.balance || 0;
                }

                const finalBal = allTransactions.length
                    ? allTransactions[allTransactions.length - 1].balance?.toFixed(2) || '0'
                    : '0';
                setFinalBalance(finalBal);

                const description = result.accountCards?.length > 0
                    ? result.accountCards[0].description
                    : accountCard.name;

                setFirstRowDetails({
                    creditAccountAmount: totalCreditAmount,
                    debitAccountAmount: totalDebitAmount,
                    balance: totalBalance,
                    description,
                });

                const dataForPdf = {
                    clientName: client.name,
                    // DISPLAY ONLY: VAT-authority dealer number for the report header.
                    clientCase: client.vatClientNumber || client.caseId,
                    caseType: client.caseType,
                    startDate: fetchStart,
                    EndDate: fetchEnd,
                    accountCard: accountCard.name,
                    accountCardId: accountCard.cardId,
                    accountCardName: accountCard.name,
                    accountCards: result.accountCards || [],
                    transactions: allTransactions,
                    creditAccountAmount: totalCreditAmount,
                    debitAccountAmount: totalDebitAmount,
                    balance: totalBalance,
                };

                setSortedDataForPdf(dataForPdf);
            } catch (err) {
                console.error('useAccountCardReport: fetch error', err);
            } finally {
                setReportLoading(false);
            }
        })();
    }, [pendingCard, client, token, isMonthly]);

    return {
        promptReport,
        fetchReport,
        closeReport,
        reportOpen,
        reportLoading,

        datePickerOpen,
        closeDatePicker,
        pickerStartDate,
        setPickerStartDate,
        pickerEndDate,
        setPickerEndDate,
        pendingCardName: pendingCard?.name || '',

        reportProps: {
            open: reportOpen,
            handleClose: closeReport,
            data: sortedDataForPdf,
            initialData: firstRowDetails,
            onSortConfigChange: handleSortConfigChange,
            token,
            finalBalance,
            palCodeCategoryOptions,
            selectedCardsForReport,
        },
    };
}
